import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../components/FaceIdentityBrandLogo';
import { useFaceIdentityOrgLogo } from '../hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../hooks/useFaceIdentityScrollShell';
import {
  getFaceUserProfile,
  clearAllGuestKioskState,
  persistFacePending,
  persistFaceProfileSnapshot,
  readFaceCaptureDataUrl,
} from '../utils/faceIdentityApi';
import { faceIdentityRoute } from '../utils/faceIdentityRoutes';
import { fetchActiveVisit } from '../../salon-admin/utils/checkinQueueApi';
import { buildKioskNavState } from '../utils/kioskNavState';
import '../styles/FaceIdentityLuxTheme.css';
import '../styles/FaceIdentityScrollShell.css';
import './FaceIdentityRegisterPage.css';

const FEATURES = [
  {
    title: 'Front desk assist',
    text: 'Our team is completing your profile at the reception.',
  },
  {
    title: 'Safe & secure',
    text: 'Your face scan and details are encrypted and protected.',
  },
  {
    title: 'Almost ready',
    text: 'This screen updates automatically when registration is complete.',
  },
];

const IconShield = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 3l8 3v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-3z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const IconLeaf = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3c.4 3.2 2.2 5.4 4.8 6.2C14.2 10 12.4 12.2 12 15.4 11.6 12.2 9.8 10 7.2 9.2 9.8 8.4 11.6 6.2 12 3z" />
  </svg>
);

export default function PassportPendingRegistrationPage() {
  useFaceIdentityScrollShell();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const captureUrl = location.state?.captureUrl || readFaceCaptureDataUrl();
  const referenceToken = location.state?.referenceToken || '';
  const checkinId = location.state?.checkinId || null;
  const [message, setMessage] = useState('Please wait at the front desk while we complete your registration.');
  const [error, setError] = useState('');

  const goWelcome = useCallback(
    async (faceUserId) => {
      try {
        const profileRes = await getFaceUserProfile(faceUserId);
        const user = profileRes?.user || { id: faceUserId };
        const profile = profileRes?.profile || user?.profile || null;
        const snapshot = {
          faceUserId,
          user,
          profile,
          captureUrl,
          mode: 'returning',
          isNewMember: true,
        };
        persistFaceProfileSnapshot(snapshot);
        persistFacePending(snapshot);
        navigate(faceIdentityRoute('welcome', location.pathname), {
          state: {
            ...snapshot,
            mode: 'frontdesk',
            passportGuestShown: true,
            checkinId: checkinId || location.state?.checkinId || undefined,
          },
          replace: true,
        });
      } catch (err) {
        setError(err?.message || 'Registration completed but profile could not be loaded.');
        setMessage('Registration complete. Please ask the front desk to assist.');
      }
    },
    [captureUrl, checkinId, location.pathname, location.state?.checkinId, navigate],
  );

  const goScanAfterCancel = useCallback(() => {
    clearAllGuestKioskState();
    setMessage('This visit was cancelled at the front desk. You can scan again when ready.');
    navigate(faceIdentityRoute('scan', location.pathname), {
      replace: true,
      state: buildKioskNavState(location.state, {
        mode: 'frontdesk',
        passportGuestShown: undefined,
        checkinId: undefined,
        referenceToken: undefined,
      }),
    });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!referenceToken && !checkinId) {
      setError('Session expired. Please ask the front desk for assistance.');
      return undefined;
    }

    let cancelled = false;
    const poll = async () => {
      try {
        const visit = await fetchActiveVisit({
          visitReference: referenceToken || undefined,
          checkinId,
          includeClosed: Boolean(checkinId),
        });
        if (cancelled) return;
        if (!visit) {
          // Still waiting / open visit not found yet — keep polling.
          return;
        }
        const status = String(visit.status || '').toLowerCase();
        if (status === 'cancelled') {
          goScanAfterCancel();
          return;
        }
        const snap = visit.customer_snapshot || {};
        const faceUserId = visit.face_user_id || snap.face_user_id || '';
        const done =
          Boolean(faceUserId) &&
          String(snap.registration_status || '').toLowerCase() === 'complete';
        if (done) {
          setMessage('Registration complete. Welcome!');
          await goWelcome(faceUserId);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Could not check registration status');
        }
      }
    };

    poll();
    const timer = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [checkinId, goScanAfterCancel, goWelcome, referenceToken]);

  return (
    <div className="fi-register fi-lux-theme fi-scroll-page">
      <div className="fi-register__stage">
        <div className="fi-register__layout">
          <aside className="fi-register__aside">
            <div className="fi-register__brand">
              <FaceIdentityBrandLogo
                showLogo={showLogo}
                orgLogoUrl={orgLogoUrl}
                onLogoError={onLogoError}
                imgClassName="fi-brand-logo__img fi-register__logo"
                fallbackClassName="fi-register__logo-text"
              />
            </div>

            {captureUrl ? (
              <div className="fi-register__portrait-wrap">
                <div className="fi-register__portrait">
                  <img src={captureUrl} alt="Your face scan" />
                </div>
                <span className="fi-register__badge" aria-hidden="true">
                  <IconLeaf />
                </span>
              </div>
            ) : null}

            <div className="fi-register__aside-copy">
              <h1 className="fi-register__aside-title">We&apos;re preparing your visit</h1>
              <p className="fi-register__aside-lede">
                Please wait at the front desk while our team completes your registration.
              </p>
            </div>

            <ul className="fi-register__features">
              {FEATURES.map((feature) => (
                <li key={feature.title} className="fi-register__feature">
                  <span className="fi-register__feature-icon" aria-hidden="true">•</span>
                  <div>
                    <p className="fi-register__feature-title">{feature.title}</p>
                    <p className="fi-register__feature-text">{feature.text}</p>
                  </div>
                </li>
              ))}
            </ul>

            <p className="fi-register__privacy">
              <IconShield />
              <span>
                Your privacy is our priority. All data is <strong>secure</strong> and{' '}
                <strong>confidential</strong>.
              </span>
            </p>
          </aside>

          <section className="fi-register__panel" aria-labelledby="fi-passport-pending-title">
            <header className="fi-register__panel-head">
              <div className="fi-register__panel-head-row">
                <FaceIdentityBrandLogo
                  showLogo={showLogo}
                  orgLogoUrl={orgLogoUrl}
                  onLogoError={onLogoError}
                  imgClassName="fi-brand-logo__img fi-brand-logo__img--panel"
                  fallbackClassName="fi-brand-logo__fallback fi-brand-logo__fallback--panel"
                />
              </div>
              <h1 id="fi-passport-pending-title" className="fi-register__panel-title">
                Front desk registration
              </h1>
              <p className="fi-register__panel-meta">{message}</p>
            </header>

            <div className="fi-register__section fi-register__waiting" aria-live="polite">
              <div className="fi-register__waiting-indicator" aria-hidden="true" />
              <p className="fi-register__waiting-text">
                Our team is entering your details. This screen will update automatically when ready.
              </p>
            </div>

            {error ? <p className="fi-register__error">{error}</p> : null}
          </section>
        </div>
      </div>
    </div>
  );
}
